import { useState } from "react";
import { useParams } from "react-router-dom";
import { Chat } from "components/chat/Chat";
import { ChannelFilters } from "stream-chat";
import { DefaultStreamChatGenerics } from "stream-chat-react/dist/types/types";
import { useChatContext } from "stream-chat-react";
import { withChatFilterContext } from "contexts/ChatFilterContext";
import { useStreamFilterQuery } from "hooks/useStreamFilterQuery";
import { ChannelHeader } from "components/ChannelHeader/ChannelHeader";
import { Button } from "components/DesignSystem/Button/Button";
import { ArrowLeft } from "components/icons/ArrowLeft";
import { useIsSmallScreen } from "hooks/useIsSmallScreen";
import { setScrollToMessageId } from "store/slices/messageToScrollTo";
import { useDispatch } from "react-redux";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { useGetGroupMembersQuery } from "store/apis/group";

type SelectedChannel = {
  groupId: string;
  channelId: string;
};

const GroupChat = () => {
  const dispatch = useDispatch();
  const { groupId } = useParams<{ groupId: string }>();
  const { channelId: paramsChannelId } = useParams<{ channelId: string }>();
  const [selectedChannel, setSelectedChannel] = useState<SelectedChannel>({
    groupId,
    channelId: paramsChannelId,
  });
  const { messageId } = useParams<{ messageId: string }>();
  const { client, setActiveChannel } = useChatContext();
  const filtersQuery = useStreamFilterQuery();
  const isSmallScreen = useIsSmallScreen();
  const {
    currentData: groupMembers,
    isFetching,
    isSuccess,
    isError,
  } = useGetGroupMembersQuery({ groupId }, { skip: !groupId });

  const isLoadingMembers = isFetching && !groupMembers;

  const channelId =
    selectedChannel.groupId === groupId
      ? selectedChannel.channelId
      : paramsChannelId;

  const { group_uuid: _companyFilter, ...chatFiltersQuery } = filtersQuery;

  const filters: ChannelFilters<DefaultStreamChatGenerics> = {
    type: "messaging",
    members: { $in: [client.userID!] },
    group_uuid: { $eq: groupId },
    ...chatFiltersQuery,
  };

  const handleBackToChannelList = () => {
    setSelectedChannel({ groupId, channelId: "" });
  };

  const onChannelSelect = (channelId: string, messageId?: string) => {
    setSelectedChannel({ groupId, channelId });
    const channelData = client.channel("messaging", channelId, {});
    setActiveChannel(channelData);
    dispatch(setScrollToMessageId(messageId));
  };

  return (
    <Async.Root
      isLoading={isLoadingMembers}
      isSuccess={isSuccess}
      isError={isError}
      isEmpty={groupMembers?.length === 0}
    >
      <Async.Empty>
        <strong>No member exists in the group!</strong>
      </Async.Empty>
      <Async.ErrorHandler>
        <strong>Failed to load group members</strong>
      </Async.ErrorHandler>
      <Async.Success>
        <Chat
          filters={filters}
          channelId={channelId}
          messageId={messageId}
          hideFilters={["COMPANY", "AGENT"]}
          onChannelSelect={onChannelSelect}
          channelHeaderProps={
            isSmallScreen
              ? {
                  Header: (props) => {
                    return (
                      <ChannelHeader
                        {...props}
                        actionsAvailable={true}
                        backButton={
                          <Button
                            size="small"
                            customType="ghost_icon"
                            onClick={handleBackToChannelList}
                          >
                            <ArrowLeft size="16px" />
                          </Button>
                        }
                      />
                    );
                  },
                }
              : {}
          }
        />
      </Async.Success>
    </Async.Root>
  );
};

export const Messages = withChatFilterContext(GroupChat);
